// ProteoformAbundances.tsx
import { DetailSection } from '@/components/DetailPage';
import { sampleQueryKeys } from '@/config/queryKeys';
import { useApiClient } from '@/hooks/useApiClient';
import { useProteoformResultsTableConfig } from '@/hooks/useAssays';
import type { PaginatedSampleProteoformsResponse, SampleProteoformPublic, SampleWithNamesPublic } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Loading } from '@nautilusbio/nb-design-system/Loading';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-enterprise';
import { Download } from 'lucide-react';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PROTEOFORM_SEARCH_BODY } from './constants';
import styles from './ProteoformAbundances.module.css';
import { parseProteoformName } from './proteoformMatrix';
import { PROTEOFORM_TOKENS } from './proteoformTokens';

export interface ProteoformAbundancesProps {
  sample: SampleWithNamesPublic;
  orgId: string;
}

// Fallback threshold + token order used only while the assay's proteoform results table
// config (POR-322 `/assays/{assay_id}/proteoform_results_table_config`) hasn't loaded yet.
const THRESHOLD_STUB = 1000;
const tableId = (sampleId: string) => `proteoforms-matrix-${sampleId}`;

function stat(label: string, value: React.ReactNode, testId?: string) {
  return (
    <Stack className={styles.stat} data-testid={testId} key={label}>
      <Text as="span" className={styles.statLabel} size="xs">
        {label}
      </Text>
      <Text as="span" size="sm">
        {value}
      </Text>
    </Stack>
  );
}

function ProteoformAbundancesComponent({ sample, orgId }: ProteoformAbundancesProps) {
  const { t } = useTranslation('studies');
  // Proteoform matrix column-header keys (sampleDetails.col_*) live in the `runs` namespace.
  const { t: tCol } = useTranslation('runs');
  const api = useApiClient();

  // POR-308 (narrow slice): isoform/PTM column set + order + labels, and the default
  // threshold, come from the assay's proteoform results table config (POR-322) instead of the
  // hardcoded PROTEOFORM_TOKENS/THRESHOLD_STUB. The sample now carries a real `assay_id`, so
  // this is a direct lookup — the old useAssays() + ASSAY_TYPE_TO_RUN_ASSAY_VERSION round-trip
  // through assay_version is gone.
  const { data: tableConfig, isLoading: isTableConfigLoading } = useProteoformResultsTableConfig(sample.assay_id);
  // Gates the threshold stat so the user never sees THRESHOLD_STUB and mistakes it for the
  // real, assay-specific value while the fetch is still resolving. `assay_id` is always
  // present on the sample now, so the config fetch is the only thing that can be in flight.
  const isThresholdLoading = isTableConfigLoading;

  // Isoform and PTM columns each have their own 1-based `index` (per the backend's
  // ProteoformResultsTableConfig docstring: "Column render order: isoform_columns (by index)
  // → ptm_columns (by index)") — sorting the *concatenated* array by index would interleave
  // isoform-index-1 with ptm-index-1 instead of rendering isoforms as a contiguous group
  // before PTMs. Sort each group separately, then concatenate.
  const knownTokensInOrder = useMemo(() => {
    if (!tableConfig) return PROTEOFORM_TOKENS.map(c => c.token);
    const isoform = tableConfig.isoform_columns.toSorted((a, b) => a.index - b.index);
    const ptm = tableConfig.ptm_columns.toSorted((a, b) => a.index - b.index);
    return [...isoform, ...ptm].map(c => c.key);
  }, [tableConfig]);

  // Fully-resolved display strings (not i18n keys) — resolves the PROTEOFORM_TOKENS fallback
  // through tCol immediately so downstream rendering doesn't need to know which source is active.
  // `as never` (not a specific literal key) bridges i18next's strongly-typed `t` — which only
  // accepts the known key union — to PROTEOFORM_TOKENS's plain `string` i18nKey field, without
  // narrowing to one specific key the way `as 'sampleDetails.col_n_terminus'` incorrectly did
  // (mirrors the same bridging pattern already used in RunSampleDetailsSection/index.tsx).
  const headerByToken = useMemo(() => {
    if (!tableConfig) {
      return new Map(PROTEOFORM_TOKENS.map(c => [c.token, tCol(c.i18nKey as never)]));
    }
    return new Map([...tableConfig.isoform_columns, ...tableConfig.ptm_columns].map(c => [c.key, c.label]));
  }, [tableConfig, tCol]);

  const { data } = useQuery({
    queryKey: sampleQueryKeys.proteoformsSearch(orgId, sample.id),
    queryFn: async () => {
      const url = sampleQueryKeys.endpoints.proteoformsSearch(orgId, sample.id);
      const response = await api.post<PaginatedSampleProteoformsResponse>(url, PROTEOFORM_SEARCH_BODY);
      if (response === null || response === undefined) throw new Error('No response from server');
      return response;
    },
    enabled: orgId !== '' && sample.id !== '',
    retry: 1,
  });

  const records = useMemo(() => data?.records ?? [], [data]);

  const columnDefs = useMemo<ColDef<SampleProteoformPublic>[]>(() => {
    const yes = t('yes', { ns: 'general' });
    const no = t('no', { ns: 'general' });

    // Parse each distinct proteoform_name once; matrix cells (rows × tokens)
    // otherwise re-split the same composite string on every render/scroll.
    const tokensByName = new Map<string, Set<string>>();
    const tokensFor = (name: string): Set<string> => {
      let parsed = tokensByName.get(name);
      if (parsed === undefined) {
        parsed = parseProteoformName(name);
        tokensByName.set(name, parsed);
      }
      return parsed;
    };

    // Column *existence* is driven entirely by the assay's known isoform/PTM columns
    // (knownTokensInOrder), not by which tokens happen to appear in the currently-loaded
    // page of records — otherwise a column can vanish (and drop out of the show/hide list)
    // just because no loaded row happens to have that token. Per-row presence still uses
    // parseProteoformName for the cell value.
    const matrixCols: ColDef<SampleProteoformPublic>[] = knownTokensInOrder.map(token => ({
      colId: token,
      headerName: headerByToken.get(token) ?? token,
      sortable: false,
      filter: false,
      valueGetter: ({ data: row }) => (row !== undefined && tokensFor(row.proteoform_name).has(token) ? yes : no),
    }));

    return [
      {
        headerName: tCol('sampleDetails.col_proteoform_group'),
        field: 'proteoform_name',
        // minWidth prevents this flex column from being squeezed unreadably narrow now that
        // the matrix can render ~14 fixed-width isoform/PTM columns alongside it (POR-425).
        flex: 1,
        minWidth: 160,
        sortable: true,
      },
      {
        headerName: tCol('sampleDetails.col_abundance'),
        field: 'proteoform_abundance',
        sortable: true,
        valueFormatter: ({ value }: { value: number | null | undefined }) =>
          value !== null && value !== undefined ? value.toLocaleString() : '',
      },
      ...matrixCols,
    ];
  }, [t, headerByToken, knownTokensInOrder, tCol]);

  const toolbar = (
    <Stack className={styles.toolbar}>
      {/* TODO(backend): no per-sample export endpoint (only run-scoped download exists). */}
      <Tooltip
        content={t('samples.details.actionComingSoon')}
        placement="bottom"
        trigger={
          <Button data-testid="download-proteoforms-btn" isDisabled size="md" visual="unstyled">
            <Download size={20} />
            <Text as="span" noWrap size="md">
              {t('samples.details.download')}
            </Text>
          </Button>
        }
      />
      {/* The three counts moved off the sample DTO and onto the proteoform search envelope —
          which this component already fetches, so no extra request. They reflect only rows
          clearing threshold_cpm, matching the threshold shown below. */}
      {stat(t('samples.details.statProteoforms'), data?.num_proteoform_groups)}
      {stat(t('samples.details.statPtms'), data?.num_ptms)}
      {stat(t('samples.details.statIsoforms'), data?.num_isoforms)}
      {/* Threshold now sourced from the assay's proteoform results table config (POR-322); pencil still disabled — no edit endpoint. */}
      {stat(
        t('samples.details.threshold'),
        isThresholdLoading ? (
          <Loading type="inline" />
        ) : (
          t('samples.details.thresholdValue', {
            value: (tableConfig?.default_threshold_cpm ?? THRESHOLD_STUB).toLocaleString(),
          })
        ),
        'proteoform-threshold-stat',
      )}
    </Stack>
  );

  return (
    <DetailSection
      data-testid="proteoform-abundances"
      title={t('samples.details.proteoformAbundancesHeading')}
      toolbar={toolbar}
    >
      <Table<SampleProteoformPublic>
        ariaLabel={t('samples.details.matrixAriaLabel')}
        columnDefs={columnDefs}
        density="compact"
        domLayout="autoHeight"
        enableResponsiveResize={false}
        pagination={data !== null && data !== undefined && data.total > data.limit}
        paginationPageSize={data?.limit}
        rowData={records}
        tableId={tableId(sample.id)}
        onGridReady={e => sizeColumnsOnFirstVisit(e.api, tableId(sample.id))}
        onGridPreDestroyed={e => flushColumnState(e.api, tableId(sample.id))}
        zebraStriping
      />
    </DetailSection>
  );
}

export const ProteoformAbundances = memo(ProteoformAbundancesComponent);
